import { createStore } from 'vuex'
import { auth, db } from '@/firebase/config'
import { doc, getDoc } from 'firebase/firestore'
import emailjs from 'emailjs-com'
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth'

// ── Login OTP settings ───────────────────────────────────────────────────────
const COMPANY_NAME = 'Decentralized Digital Banking'
const OTP_VALID_MINUTES = 15
const OTP_MAX_ATTEMPTS = 5
const OTP_SESSION_KEY = 'otpVerifiedUid'

const EMAILJS_SERVICE_ID = 'service_qultz3r'
const EMAILJS_TEMPLATE_ID = 'template_c5u605d'
const EMAILJS_PUBLIC_KEY = 'PnQtfoVbRxtIDD2rp'

// Cryptographically random 6-digit code (always 100000-999999)
const generateOtp = () => {
  const buf = new Uint32Array(1)
  window.crypto.getRandomValues(buf)
  return String(100000 + (buf[0] % 900000))
}

const formatTime = (timestamp) =>
    new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(new Date(timestamp))

// Optional code an admin can save on a user (UserTable.otp) from the admin panel
const fetchAdminIssuedOtp = async (uid) => {
  try {
    const snap = await getDoc(doc(db, 'UserTable', uid))
    return snap.exists() ? String(snap.data().otp || '') : ''
  } catch (err) {
    console.error('Could not read admin-issued OTP:', err)
    return ''
  }
}

const store = createStore({
  state: {
    user: null,
    authIsReady: false,
    Approved: false,
    userData: "account1",
    digitalCurrency: "",
    transferForm: {},
    signupFormData: null,
    forgotPasswordFormData: null,

    // Login OTP (kept in memory only)
    loginOtp: null, // { code, email, expiresAt, sentAt, attempts }
    // UID of the user who completed the OTP step in this browser session
    otpVerifiedUid: sessionStorage.getItem(OTP_SESSION_KEY) || null,
  },

  mutations: {
    setUser(state, payload) { state.user = payload },
    setAuthIsReady(state, payload) { state.authIsReady = payload },
    changed(state, payload) { state.Approved = payload },
    setUserData(state, payload) { state.userData = payload },
    setDigitalCurrency(state, payload) { state.digitalCurrency = payload },
    setTransferForm(state, payload) { state.transferForm = payload },
    setSignupFormData(state, payload) { state.signupFormData = payload },
    setForgotPasswordFormData(state, payload) { state.forgotPasswordFormData = payload },
    clearSignupFormData(state) { state.signupFormData = null },

    setLoginOtp(state, payload) { state.loginOtp = payload },
    incrementOtpAttempts(state) { if (state.loginOtp) state.loginOtp.attempts++ },
    clearLoginOtp(state) { state.loginOtp = null },
    setOtpVerifiedUid(state, uid) {
      state.otpVerifiedUid = uid
      if (uid) sessionStorage.setItem(OTP_SESSION_KEY, uid)
      else sessionStorage.removeItem(OTP_SESSION_KEY)
    },
  },

  getters: {
    finalChange: state => state.Approved,
    getUserData: state => state.userData,
    getDigitalCurrency: state => state.digitalCurrency,
    getTransferForm: state => state.transferForm,
    getSignupFormData: state => state.signupFormData,
    getForgotPasswordFormData: state => state.forgotPasswordFormData,
    // alias: ForgotPasscodeVerifyEmail.vue still reads this name
    getSetForgotPasswordFormData: state => state.forgotPasswordFormData,

    isOtpVerified: state => !!state.user && state.otpVerifiedUid === state.user.uid,
  },

  actions: {
    async signup({ commit }, { email, password }) {
      const res = await createUserWithEmailAndPassword(auth, email, password)
      if (res.user) {
        commit('setUser', res.user)
        localStorage.setItem('userId', res.user.uid)
        await sendEmailVerification(res.user)
      } else {
        throw new Error('Could not complete signup')
      }
    },

    async login({ commit }, { email, password }) {
      const res = await signInWithEmailAndPassword(auth, email, password)
      if (res.user) {
        commit('setUser', res.user)
        commit('setOtpVerifiedUid', null) // every login must pass the code step
        commit('clearLoginOtp')
        localStorage.setItem('userId', res.user.uid)
      } else {
        throw new Error('Could not complete login')
      }
    },

    async logout({ commit }) {
      await signOut(auth)
      localStorage.clear()
      commit('setUser', null)
      commit('clearSignupFormData')
      commit('clearLoginOtp')
      commit('setOtpVerifiedUid', null)
    },

    async resetPassword(_, { email }) {
      await sendPasswordResetEmail(auth, email)
    },

    changed({ commit }, payload) { commit('changed', payload) },
    fetchUserData({ commit }, payload) { commit('setUserData', payload) },
    saveSignupFormData({ commit }, payload) { commit('setSignupFormData', payload) },
    // Was missing before: used by the Forgot Passcode pages
    saveForgotPasswordFormData({ commit }, payload) { commit('setForgotPasswordFormData', payload) },

    fetchDigitalCurrency({ commit }, currency) {
      commit('setDigitalCurrency', currency)
    },

    // Used by the Send Money flow (SendMoneyForm, TransferAmountCard):
    // keeps the recipient details, amount, note and PIN for the confirm modal
    fetchTransferForm({ commit }, payload) {
      commit('setTransferForm', payload)
    },

    // ── Login OTP ────────────────────────────────────────────────────────────

    // Generates a code, emails it to the signed-in user and remembers it in memory.
    // Throws if the user is not signed in or the email could not be sent.
    async sendLoginOtp({ commit }) {
      const user = auth.currentUser
      if (!user || !user.email) throw new Error('No signed-in user')

      const code = generateOtp()
      const sentAt = Date.now()
      const expiresAt = sentAt + OTP_VALID_MINUTES * 60 * 1000

      await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            email: user.email,
            subject: `OTP for your ${COMPANY_NAME} authentication`,
            companyName: COMPANY_NAME,
            name: '',
            message:
                `To authenticate, please use the following One Time Password (OTP):\n\n${code}\n\n` +
                `This OTP will be valid for ${OTP_VALID_MINUTES} minutes till ${formatTime(expiresAt)}.\n\n` +
                `Do not share this OTP with anyone. If you didn't make this request, you can safely ignore this email.\n\n` +
                `${COMPANY_NAME} will never contact you about this email or ask for any login codes or links. Beware of phishing scams.\n\n` +
                `Thanks for visiting ${COMPANY_NAME}!`
          },
          EMAILJS_PUBLIC_KEY
      )

      // Only store the code once the email has really been sent
      commit('setLoginOtp', { code, email: user.email, expiresAt, sentAt, attempts: 0 })
    },

    // Returns { ok: true } or { ok: false, reason, attemptsLeft? }
    // reason: 'no-session' | 'none' | 'expired' | 'incorrect' | 'locked'
    async verifyLoginOtp({ commit, state }, enteredCode) {
      const user = auth.currentUser
      if (!user) return { ok: false, reason: 'no-session' }

      const entered = String(enteredCode).trim()
      const otp = state.loginOtp
      const now = Date.now()

      const succeed = () => {
        commit('setOtpVerifiedUid', user.uid)
        commit('clearLoginOtp') // single use
        return { ok: true }
      }

      // 1. The code we emailed
      if (otp && now <= otp.expiresAt && entered === otp.code) return succeed()

      // 2. A code the admin saved on this user (optional fallback)
      const adminOtp = await fetchAdminIssuedOtp(user.uid)
      if (adminOtp && entered === adminOtp) return succeed()

      // Failed
      if (!otp) return { ok: false, reason: 'none' }
      if (now > otp.expiresAt) return { ok: false, reason: 'expired' }

      commit('incrementOtpAttempts')
      const attemptsLeft = OTP_MAX_ATTEMPTS - state.loginOtp.attempts
      if (attemptsLeft <= 0) {
        commit('clearLoginOtp')
        return { ok: false, reason: 'locked' }
      }
      return { ok: false, reason: 'incorrect', attemptsLeft }
    },
  },

  modules: {}
})

// Resolves once Firebase has told us whether someone is signed in (used by the router guard)
let resolveAuthReady
export const authReady = new Promise((resolve) => { resolveAuthReady = resolve })

const unsub = onAuthStateChanged(auth, (user) => {
  store.commit('setAuthIsReady', true)
  store.commit('setUser', user)
  store.commit('changed', true)
  resolveAuthReady()
  unsub()
})

export default store